import React, { useEffect, useState, useContext } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import secureAxios from "../../utils/secureAxios";

const BranchList = ({ onEdit, refreshTrigger }) => {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filterCustomer, setFilterCustomer] = useState("");
  const [customers, setCustomers] = useState([]);
  const [showInactive, setShowInactive] = useState(false);

  const { user } = useContext(AuthContext);
  const tenant_id = user?.tenant_id;

  useEffect(() => {
    if (!tenant_id) return;

    // Load customers for filter
    secureAxios
      .get(`/api/customers?tenant_id=${tenant_id}`)
      .then((res) => setCustomers(res.data))
      .catch((err) => console.error("Error loading customers:", err));
  }, [tenant_id]);

  useEffect(() => {
    fetchBranches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant_id, filterCustomer, refreshTrigger]);

  const fetchBranches = async () => {
    if (!tenant_id) return;

    setLoading(true);
    setError("");

    try {
      let url = `/api/branches?tenant_id=${tenant_id}`;
      if (filterCustomer) {
        url += `&customer_id=${filterCustomer}`;
      }

      const res = await secureAxios.get(url);
      setBranches(res.data);
    } catch (err) {
      console.error("Error fetching branches:", err);
      setError("Failed to load branches");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (branch) => {
    if (
      !window.confirm(
        `Are you sure you want to deactivate "${branch.branch_name}"?`
      )
    ) {
      return;
    }

    try {
      await secureAxios.delete(
        `/api/branches/${branch.branch_id}?tenant_id=${tenant_id}`
      );
      fetchBranches();
    } catch (err) {
      console.error("Error deleting branch:", err);
      const message =
        err.response?.data?.error || "Failed to delete branch";
      alert(message);
    }
  };

  const filteredBranches = showInactive
    ? branches
    : branches.filter((b) => b.is_active);

  if (loading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="alert alert-danger">{error}</div>;
  }

  return (
    <div>
      {/* Filters */}
      <div className="row mb-3 g-2">
        <div className="col-12 col-md-4">
          <label className="form-label">Filter by Customer</label>
          <select
            className="form-select"
            value={filterCustomer}
            onChange={(e) => setFilterCustomer(e.target.value)}
          >
            <option value="">All Customers</option>
            {customers.map((c) => (
              <option key={c.customer_id} value={c.customer_id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-12 col-md-4 d-flex align-items-end mt-2 mt-md-0">
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="showInactive"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="showInactive">
              Show Inactive
            </label>
          </div>
        </div>
      </div>

      {/* Empty State */}
      {filteredBranches.length === 0 ? (
        <div className="alert alert-info">
          No branches found. Create one using the tab above.
        </div>
      ) : (
        <>
          {/* Desktop/Tablet Table View */}
          <div className="table-responsive d-none d-md-block">
            <table className="table table-striped table-hover">
              <thead className="table-dark">
                <tr>
                  <th>Branch/Office Name</th>
                  <th>Customer</th>
                  <th>Description</th>
                  <th>Status</th>
                  {onEdit && <th style={{ width: "150px" }}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {filteredBranches.map((branch) => (
                  <tr
                    key={branch.branch_id}
                    className={!branch.is_active ? "table-secondary" : ""}
                  >
                    <td>
                      <i className="bi bi-building me-2"></i>
                      {branch.branch_name}
                    </td>
                    <td>{branch.customer_name || "All Customers"}</td>
                    <td>
                      {branch.description ? (
                        <span
                          className="text-muted"
                          title={branch.description}
                          style={{
                            maxWidth: "200px",
                            display: "inline-block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {branch.description}
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                      {branch.is_active ? (
                        <span className="badge bg-success">Active</span>
                      ) : (
                        <span className="badge bg-secondary">Inactive</span>
                      )}
                    </td>
                    {onEdit && (
                      <td>
                        <button
                          className="btn btn-sm btn-outline-primary me-1"
                          onClick={() => onEdit(branch.branch_id)}
                          title="Edit"
                          aria-label={`Edit ${branch.branch_name}`}
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                        {branch.is_active && (
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(branch)}
                            title="Deactivate"
                            aria-label={`Deactivate ${branch.branch_name}`}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="d-block d-md-none">
            {filteredBranches.map((branch) => (
              <div
                key={branch.branch_id}
                className={`card mb-3 ${!branch.is_active ? "bg-light" : ""}`}
              >
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <h6 className="card-title mb-0">
                      <i className="bi bi-building me-2"></i>
                      {branch.branch_name}
                    </h6>
                    {branch.is_active ? (
                      <span className="badge bg-success">Active</span>
                    ) : (
                      <span className="badge bg-secondary">Inactive</span>
                    )}
                  </div>

                  <div className="mb-2">
                    <small className="text-muted">Customer:</small>
                    <div>{branch.customer_name || "All Customers"}</div>
                  </div>

                  {branch.description && (
                    <div className="mb-2">
                      <small className="text-muted">Description:</small>
                      <div className="text-muted small">{branch.description}</div>
                    </div>
                  )}

                  {onEdit && (
                    <div className="d-flex gap-2 mt-3 pt-2 border-top">
                      <button
                        className="btn btn-sm btn-primary flex-grow-1"
                        onClick={() => onEdit(branch.branch_id)}
                        aria-label={`Edit ${branch.branch_name}`}
                      >
                        <i className="bi bi-pencil me-1"></i> Edit
                      </button>
                      {branch.is_active && (
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(branch)}
                          aria-label={`Deactivate ${branch.branch_name}`}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="text-muted small mt-2">
        Showing {filteredBranches.length} of {branches.length} branches
      </div>
    </div>
  );
};

export default BranchList;